import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Check, Eye, EyeOff } from "lucide-react";
import { api, formatApiError, resolveMediaUrl } from "@/lib/api";
import FileUpload from "@/components/admin/FileUpload";

const empty = { title: "", poster: "", description: "", published: true, posted_at: "" };

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const AdminCareers = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/careers").then(({ data }) => setItems(Array.isArray(data) ? data : []));
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing("new");
    setForm({ ...empty, posted_at: toLocalInput(new Date().toISOString()) });
  };

  const openEdit = (c) => {
    setEditing(c.id);
    setForm({
      title: c.title || "",
      poster: c.poster || "",
      description: c.description || "",
      published: !!c.published,
      posted_at: toLocalInput(c.posted_at || c.created_at),
    });
  };

  const save = async () => {
    if (!form.title || !form.poster) {
      toast.error("Judul dan poster wajib diisi");
      return;
    }
    try {
      const payload = {
        title: form.title,
        poster: form.poster,
        description: form.description || "",
        published: !!form.published,
        posted_at: form.posted_at ? new Date(form.posted_at).toISOString() : new Date().toISOString(),
      };
      if (editing === "new") await api.post("/careers", payload);
      else await api.put(`/careers/${editing}`, payload);
      toast.success("Berhasil disimpan");
      setEditing(null);
      setForm(empty);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail || e.response?.data));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus lowongan ini?")) return;
    await api.delete(`/careers/${id}`);
    toast.success("Terhapus");
    load();
  };

  const toggle = async (c) => {
    await api.put(`/careers/${c.id}`, {
      title: c.title,
      poster: c.poster,
      description: c.description || "",
      published: !c.published,
      posted_at: c.posted_at,
    });
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-display font-bold text-zinc-900">Karir</h1>
          <p className="mt-1 text-sm text-zinc-500">Upload poster lowongan kerja. Tampil di website berurut tanggal terbaru.</p>
        </div>
        <button onClick={openNew} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-maroon-800 text-white font-semibold">
          <Plus size={16} /> Tambah Lowongan
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((c) => (
          <div key={c.id} className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
            <div className="aspect-[3/4] bg-zinc-100">
              <img src={resolveMediaUrl(c.poster)} alt={c.title} className="w-full h-full object-cover" />
            </div>
            <div className="p-4">
              <div className="text-xs text-zinc-500">
                {c.posted_at ? new Date(c.posted_at).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" }) : "-"}
              </div>
              <h3 className="mt-1 font-display font-bold text-zinc-900 line-clamp-2">{c.title}</h3>
              <div className="mt-3 flex items-center justify-between">
                <span className={`text-xs font-semibold ${c.published ? "text-maroon-700" : "text-zinc-400"}`}>
                  {c.published ? "Aktif" : "Draft"}
                </span>
                <div className="flex gap-1">
                  <button onClick={() => toggle(c)} className="p-2 rounded-lg hover:bg-zinc-100" title="Publish/Unpublish">
                    {c.published ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                  <button onClick={() => openEdit(c)} className="p-2 rounded-lg hover:bg-zinc-100"><Pencil size={14} /></button>
                  <button onClick={() => del(c.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-600"><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="col-span-full p-12 text-center text-zinc-500 bg-white rounded-2xl border border-zinc-200">
            Belum ada poster lowongan. Klik “Tambah Lowongan”.
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-xl max-h-[92vh] overflow-y-auto">
            <div className="sticky top-0 bg-white flex items-center justify-between p-6 border-b">
              <h3 className="text-xl font-display font-bold">{editing === "new" ? "Tambah Lowongan" : "Edit Lowongan"}</h3>
              <button onClick={() => setEditing(null)} className="p-2 rounded-lg hover:bg-zinc-100"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              <Field label="Judul posisi *">
                <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-zinc-200 outline-none focus:border-maroon-700" />
              </Field>
              <FileUpload label="Poster lowongan *" value={form.poster} onChange={(v) => setForm({ ...form, poster: v })} folder="careers" aspect="portrait" />
              <Field label="Tanggal upload / tayang">
                <input type="datetime-local" value={form.posted_at} onChange={(e) => setForm({ ...form, posted_at: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-zinc-200 outline-none focus:border-maroon-700" />
              </Field>
              <Field label="Keterangan (opsional)">
                <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-zinc-200 outline-none focus:border-maroon-700" />
              </Field>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} />
                Tampilkan di website
              </label>
            </div>
            <div className="sticky bottom-0 bg-white flex justify-end gap-3 p-6 border-t">
              <button onClick={() => setEditing(null)} className="px-5 py-2.5 rounded-full border font-semibold">Batal</button>
              <button onClick={save} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-maroon-800 text-white font-semibold">
                <Check size={16} /> Simpan
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="block text-sm font-semibold text-zinc-800 mb-2">{label}</label>
    {children}
  </div>
);

export default AdminCareers;
